/**
 * The combat focus overlay. ONE component, ONE mounted panel for the
 * whole life of a fight, on BOTH clients. The panel is keyed by
 * combatId and morphs between modes instead of unmounting, so the
 * stage can never "disappear" mid-fight:
 *
 *   assign    the acting player splits the attacker's power across the
 *             defenders (pip rail, click-to-assign, Auto, Strike)
 *   spectate  the other player watches the SAME panel live. Amounts
 *             stream in via combat:assign_preview broadcasts
 *   locked    the acting player after Strike, while the host resolves
 *   playback  the blows land as staged beats ON THIS PANEL. See below.
 *             Plays for every fight (1v1, multi-defender, unit-vs-site);
 *             fights without an assign step open directly in this mode.
 *
 * Playback beat anatomy (one per damage event):
 *   windup    the striking card pulls back and rises
 *   dash      it physically crosses the panel into the struck card
 *             (offsets measured live between the two portraits)
 *   impact    flash + shockwave + sparks at the contact, the victim is
 *             knocked back along the strike vector, the whole panel
 *             shakes (scaled by damage), the tiered impact sample
 *             plays (attack-N, or magic-N for Spellcaster strikers.
 *             the `magic` flag is enriched host-side into each event)
 *   return    the striker settles back into its slot
 * Strike-backs arrive as one aggregated hit on the attacker, so every
 * defender portrait dashes at once. A card whose final hit kills it
 * collapses under a DESTROYED stamp half a beat later.
 *
 * Events are pre-enriched by the host (targetName / targetImageUrl /
 * targetDied / magic) before broadcast, because killed cards are
 * already swept off the board when the stream publishes. The overlay
 * must be able to show a card that no longer exists anywhere else.
 * Click anywhere to skip playback.
 */
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import { memo } from 'preact/compat';
import { motion, AnimatePresence } from 'framer-motion';
import StoneButton from './StoneButton';
import { playSound, combatImpactKey } from '../../utils/game/sounds';
import { getAccessibilitySettings } from '../../utils/game/accessibilitySettings';
import { autoAssignDamage } from '../../utils/game/engine/core';
import FoilCanvas from '../FoilCanvas';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { isFoilFinish } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import { t } from '../../localization/t';
import {
  ACCENT_GOLD, CONTENT_BG_DEFAULT, RADIUS,
  TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE,
  FourCorners, OrnamentalDivider,
} from '../../lib/medievalTheme';

const Z = 1200;

const LEAD_MS = 500;            // entrance settle before the first beat
const BEAT_MS = 950;            // per-event beat
const HOLD_MS = 1300;           // settle time after the last beat
const DASH_MS = BEAT_MS * 0.8;  // windup + dash + return of the striker
const IMPACT_FRAC = 0.42;       // point in the dash where the hit lands
const BIG_HIT = 4;

// Wall-clock length of a full playback for n events at a given speed
// multiplier. The host keeps the resolved combat record alive for this
// window before clearing it.
export function playbackDurationMs(eventCount, speed = 1) {
  return (LEAD_MS + eventCount * BEAT_MS + HOLD_MS) / speed;
}

export const OPAQUE_BASE = '#0e0c08';
export const STONE_BG = `linear-gradient(180deg, rgba(40,30,15,0.55) 0%, rgba(15,10,4,0.85) 100%), ${CONTENT_BG_DEFAULT}`;

export const CARD_W = 12.5; // cqw. Focus-screen scale, true card aspect (~0.72)
export const CARD_H = 17.5; // cqw

// ── Fixed stage geometry (cqw; the 16:9 canvas is 100 × 56.25) ──
// The panel NEVER changes size: participants scale to fit it, the
// panel doesn't grow to fit them. 78cqw centered spans 11..89cqw,
// clearing the player-HUD rail on the right (panels are 8.75cqw wide
// inset 0.75cqw → the rail starts at 90.5cqw). 50cqw tall leaves
// ~3cqw of letterbox above and below.
const PANEL_W = 78;
const PANEL_H = 50;
// The struck-side box the defender grid must always fit inside.
// PANEL_W/H minus chrome (panel + arena padding, attacker column, VS,
// title, pip rail, footer), held slightly conservative so the grid
// can never overflow into a scroll.
const DEF_W = 41;
const DEF_H = 28;
const COL_W = CARD_W + 2;  // portrait column width (NameBlock is CARD_W + 2)
const COL_GAP = 2.2;       // between defender columns, scales with the cards
const ROW_GAP = 1.2;       // between defender rows, scales with the cards

// Rendered height of one defender column at scale s. Must mirror the
// actual markup: portrait, NameBlock (its fonts and height clamp so
// captions stay legible on small cards), and the reserved +/- slot.
const nameBlockH = (s) => Math.max(3.8 * s, 2) + 1.9 * s;
const buttonSlotH = (s) => Math.max(2, 3 * s) + 0.5;
const defColH = (s, withButtons) =>
  CARD_H * s + nameBlockH(s) + (withButtons ? buttonSlotH(s) : 0);

// Largest uniform scale (and the column count that achieves it) at
// which n defender columns fit the fixed DEF_W × DEF_H box. The clamps
// in defColH make height non-linear in s, so each candidate column
// count binary-searches its max scale instead of solving linearly.
function fitDefenders(n, withButtons) {
  if (n <= 0) return { scale: 1, cols: 1 };
  let best = { scale: 0, cols: 1 };
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
      const s = (lo + hi) / 2;
      const w = (cols * COL_W + (cols - 1) * COL_GAP) * s;
      const h = rows * defColH(s, withButtons) + (rows - 1) * ROW_GAP * s;
      if (w <= DEF_W && h <= DEF_H) lo = s; else hi = s;
    }
    if (lo > best.scale) best = { scale: Math.min(1, lo), cols };
  }
  return best;
}

const PHYSICAL_FX = 'rgb(255, 196, 110)';
const MAGIC_FX = 'rgb(186, 110, 255)';

export function CardPortrait({ name, imageUrl, dead, children, scale = 1, foiling = null, isSite = false }) {
  return (
    <div style={{ position: 'relative', width: `${CARD_W * scale}cqw`, height: `${CARD_H * scale}cqw` }}>
      <div style={{
        position: 'absolute', inset: 0,
        borderRadius: RADIUS.card,
        overflow: 'hidden',
        backgroundColor: OPAQUE_BASE,
        boxShadow: dead
          ? '0 0 0 2px rgb(214, 69, 69), 0 0.4cqw 1.2cqw rgba(0,0,0,0.7), 0 0 1.2cqw rgba(214, 69, 69, 0.5)'
          : '0 0 0 1px rgba(212, 168, 67, 0.45), 0 0.4cqw 1.2cqw rgba(0,0,0,0.7)',
        transition: 'box-shadow 140ms ease-out, filter 140ms ease-out',
        filter: dead ? 'saturate(0.45) brightness(0.75)' : 'none',
      }}>
        {imageUrl ? (
          <img src={imageUrl} alt={name} draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: ACCENT_GOLD, fontSize: '0.9cqw',
            textAlign: 'center', padding: '0.4cqw',
          }}>{name}</div>
        )}
        {/* Foil: a per-card FoilCanvas mounted as a child of the rounded,
            overflow-hidden inner div, so it rides the card's exact framer
            transform (Fighter springs, knockback, dash) and rounds with it.
            Capped by FOIL_CANVAS_MAX; only the few combat portraits use it. */}
        {imageUrl && isFoilFinish(foiling) ? (
          <FoilCanvas imageUrl={resolveLocalImageUrl(imageUrl)} zones={foilZonesForCard({ isSite })} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} />
        ) : null}
      </div>
      {children}
    </div>
  );
}

// Diagonal red stamp: LETHAL while forecasting, DESTROYED when it lands.
// Shrinks with its card but bottoms out at readable. It's the single
// most important word on the stage.
export function FateStamp({ label, scale = 1 }) {
  const k = Math.max(0.55, scale);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.6, rotate: -14 }}
      animate={{ opacity: 1, scale: 1, rotate: -14 }}
      transition={{ type: 'spring', stiffness: 700, damping: 22 }}
      style={{
        position: 'absolute', left: '50%', top: '42%',
        transform: 'translate(-50%, -50%)',
        padding: `${0.3 * k}cqw ${1.1 * k}cqw`,
        border: `${0.22 * k}cqw solid rgb(214, 69, 69)`,
        borderRadius: `${0.3 * k}cqw`,
        color: 'rgb(232, 96, 96)',
        backgroundColor: 'rgba(20, 6, 6, 0.82)',
        fontFamily: "'FantaisieArtistique', cursive",
        fontSize: `${2 * k}cqw`,
        pointerEvents: 'none',
        textShadow: '0 1px 3px rgba(0,0,0,0.9)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </motion.div>
  );
}

// Fixed-height two-line caption under every portrait: card name on a
// single non-wrapping line (ellipsized), role / defense beneath it in
// small caps. The fixed height is what keeps the +/- button rows of
// every defender column on one baseline regardless of name length.
export function NameBlock({ name, caption, scale = 1 }) {
  return (
    <div style={{
      // Heights and fonts clamp rather than scale to zero so captions
      // stay legible on heavily shrunken defender grids. Keep in sync
      // with nameBlockH above. The fit solver measures this markup.
      height: `${Math.max(3.8 * scale, 2)}cqw`,
      marginTop: `${1.9 * scale}cqw`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15cqw',
      width: `${COL_W * scale}cqw`,
    }}>
      <div style={{
        color: ACCENT_GOLD, fontSize: `${Math.max(0.75, 1.15 * scale)}cqw`, fontWeight: 700,
        maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}>{name}</div>
      <div style={{
        color: 'rgb(150, 132, 100)', fontSize: `${Math.max(0.6, 0.85 * scale)}cqw`, fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 }}>{caption}</div>
 </div>
 );
}

export function StatBadge({ value, label, color = ACCENT_GOLD, big = false, scale = 1 }) {
 // The badge carries the assigned/taken number. Clamp so it never
 // shrinks below tappable/readable even on a dense defender grid.
 const size = Math.max(2.2, (big ? 4.2 : 3.2) * scale);
 return (
 <div style={{
 position: 'absolute', bottom: `${-1.1 * scale}cqw`, left: '50%', transform: 'translateX(-50%)',
      minWidth: `${size}cqw`, height: `${size}cqw`,
      padding: '0 0.6cqw',
      borderRadius: '50%',
      backgroundColor: OPAQUE_BASE,
      backgroundImage: STONE_BG,
      boxShadow: '0 0 0 0.08cqw rgba(232, 195, 110, 0.85), 0 0 0 0.2cqw rgba(0,0,0,0.6), 0 0.25cqw 0.6cqw rgba(0,0,0,0.65)',
      color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: `${Math.max(1.05, (big ? 2 : 1.4) * scale)}cqw`,
      textShadow: '0 1px 2px rgba(0,0,0,0.9)',
    }} aria-label={label}>
      {value}
    </div>
  );
}

// IMPORTANT (every keyframe animation in this file): framer-motion
// compares `animate` targets by reference, so a keyframe ARRAY rebuilt
// during a re-render reads as "changed" and RESTARTS the animation from
// frame zero. The impact moment re-renders the panel four times mid-
// dash. Without stable references the striker's dash visibly replays.
// Hence: constants for static keyframes, useMemo keyed per beat for
// measured ones, and memo() around the FX subtree.
const BURST_ANIM = { opacity: [0, 1, 1, 0], y: ['-1cqw', '-3.4cqw', '-5cqw', '-6.4cqw'], scale: [0.6, 1.5, 1.25, 1.1] };
const BURST_ANIM_BIG = { opacity: [0, 1, 1, 0], y: ['-1cqw', '-3.4cqw', '-5cqw', '-6.4cqw'], scale: [0.6, 1.9, 1.5, 1.1] };

// Large damage number popping off a portrait during playback beats.
// The rise keyframes stay the shared constants (identity rule above);
// only the static fontSize follows the card's scale, with a floor.
function DamageBurst({ amount, warded = false, deathBlow = false, scale = 1, speed = 1 }) {
  const big = amount >= BIG_HIT || deathBlow;
  // A warded hit deals no damage. Show "Warded" in shield-steel instead
  // of a red damage number. The strike still landed; the ward absorbed it.
  // A death blow also applies no life (there is none left). Show the
  // fight-ending stamp instead of "-0".
  const color = warded ? 'rgb(122, 196, 232)' : '#ef4444';
  const label = deathBlow ? t('Death Blow') : (warded ? t('Warded') : `-${amount}`);
  return (
    <motion.div
      initial={{ opacity: 0, y: '0cqw', scale: 0.6 }}
      animate={big ? BURST_ANIM_BIG : BURST_ANIM}
      transition={{ duration: 1.1 / speed, times: [0, 0.15, 0.6, 1] }}
      style={{
        position: 'absolute', left: '50%', top: 0, x: '-50%',
        color,
        fontFamily: "'FantaisieArtistique', cursive",
        fontSize: `${(warded || deathBlow) ? Math.max(1.6, 2.4 * scale) : Math.max(2.4, (big ? 5 : 4.2) * scale)}cqw`, fontWeight: 900,
        textShadow: `0 0.1cqw 0.3cqw rgba(0,0,0,0.95), 0 0 0.8cqw ${color}`,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 8,
      }}
    >
      {label}
    </motion.div>
  );
}

// Impact effects at the struck portrait: a screen-filling flash inside
// the card, a shockwave ring, sparks flying outward, and a slash streak
// (physical) or arcane ring (magic). Re-keyed per impact so every hit
// replays from frame zero.
const ImpactFX = memo(function ImpactFX({ impact, scale = 1, speed = 1 }) {
  const sparks = useMemo(() => {
    if (!impact) return [];
    // Magic detonations scale spark density and reach with the visual
    // tier (1..7); physical hits keep the simpler big/small split.
    const tier = Math.min(7, Math.max(1, impact.amount));
    const n = impact.magic ? 8 + tier * 2 : (impact.amount >= BIG_HIT ? 14 : 9);
    const reach = impact.magic ? 2.8 + tier * 0.7 : (3.4 + (impact.amount >= BIG_HIT ? 1.6 : 0));
    return Array.from({ length: n }, (_, k) => {
      const angle = (k / n) * Math.PI * 2 + Math.random() * 0.6;
      const dist = reach + Math.random() * 3.2;
      return {
        id: k,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        size: 0.3 + Math.random() * 0.32,
        dur: (0.4 + Math.random() * 0.25) / speed,
      };
    });
  }, [impact?.key, speed]);
  if (!impact) return null;
  const color = impact.magic ? MAGIC_FX : PHYSICAL_FX;
  return (
    <div key={impact.key} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 7 }}>
      {/* Flash filling the card face */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.9, 0] }}
        transition={{ duration: 0.3 / speed, times: [0, 0.2, 1] }}
        style={{
          position: 'absolute', inset: 0, borderRadius: RADIUS.card,
          background: `radial-gradient(circle at 50% 45%, ${color} 0%, transparent 75%)`,
          mixBlendMode: 'screen',
        }}
      />
      {/* Shockwave ring */}
      <motion.div
        initial={{ opacity: 0.85, scale: 0.25 }}
        animate={{ opacity: 0, scale: 1.9 }}
        transition={{ duration: 0.45 / speed, ease: 'easeOut' }}
        style={{
          position: 'absolute', left: '50%', top: '45%',
          width: `${CARD_W * scale * 0.9}cqw`, height: `${CARD_W * scale * 0.9}cqw`,
          marginLeft: `${-CARD_W * scale * 0.45}cqw`, marginTop: `${-CARD_W * scale * 0.45}cqw`,
          borderRadius: '50%',
          border: `0.22cqw solid ${color}`,
          boxShadow: `0 0 1cqw ${color}`,
        }}
      />
      {/* Sparks */}
      {sparks.map((s) => (
        <motion.div
          key={s.id}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
          animate={{ opacity: 0, x: `${s.x}cqw`, y: `${s.y}cqw`, scale: 0.3 }}
          transition={{ duration: s.dur, ease: 'easeOut' }}
          style={{
            position: 'absolute', left: '50%', top: '45%',
            width: `${s.size}cqw`, height: `${s.size}cqw`,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: `0 0 0.5cqw ${color}`,
          }}
        />
      ))}
      {impact.magic ? (
        // Arcane circle blooming over the card. Sized to the visual
        // tier (1..7; the bolt grows in step). Tier 5+ adds a second,
        // wider shockwave ring chasing the first.
        (() => {
          const tier = Math.min(7, Math.max(1, impact.amount));
          const ring = CARD_W * scale * (0.5 + tier * 0.14);
          const outer = ring * 1.45;
          return (
            <>
              <motion.div
                initial={{ opacity: 0.9, scale: 0.2, rotate: 0 }}
                animate={{ opacity: 0, scale: 1.25, rotate: 50 }}
                transition={{ duration: 0.55 / speed, ease: 'easeOut' }}
                style={{
                  position: 'absolute', left: '50%', top: '45%',
                  width: `${ring}cqw`, height: `${ring}cqw`,
                  marginLeft: `${-ring / 2}cqw`, marginTop: `${-ring / 2}cqw`,
                  borderRadius: '50%',
                  border: `0.14cqw dashed ${color}`,
                  boxShadow: `inset 0 0 1.2cqw ${color}`,
                }}
              />
              {tier >= 5 && (
                <motion.div
                  initial={{ opacity: 0.7, scale: 0.2, rotate: 0 }}
                  animate={{ opacity: 0, scale: 1.4, rotate: -35 }}
                  transition={{ duration: 0.7 / speed, ease: 'easeOut', delay: 0.08 / speed }}
                  style={{
                    position: 'absolute', left: '50%', top: '45%',
                    width: `${outer}cqw`, height: `${outer}cqw`,
                    marginLeft: `${-outer / 2}cqw`, marginTop: `${-outer / 2}cqw`,
                    borderRadius: '50%',
                    border: `0.1cqw dashed ${color}`,
                    boxShadow: `0 0 1.6cqw ${color}`,
                  }}
                />
              )}
            </>
          );
        })()
      ) : (
        // Slash streak across the card face
        <motion.div
          initial={{ opacity: 0, scaleX: 0, rotate: -32 }}
          animate={{ opacity: [0, 1, 0], scaleX: [0.1, 1.15, 1.2], rotate: -32 }}
          transition={{ duration: 0.32 / speed, times: [0, 0.35, 1], ease: 'easeOut' }}
          style={{
            position: 'absolute', left: '-12%', top: '45%',
            width: '124%', height: '0.34cqw',
            transformOrigin: 'center',
            background: `linear-gradient(90deg, transparent 0%, #fff 35%, ${color} 65%, transparent 100%)`,
            boxShadow: `0 0 0.6cqw ${color}`,
          }}
        />
      )}
    </div>
  );
// The impact object is written once per beat, so identity equality is
// exactly "same impact." Parent re-renders (deaths, bursts expiring)
// can't restart the in-flight flash/sparks/ring.
}, (prev, next) => prev.impact === next.impact && prev.scale === next.scale && prev.speed === next.speed);

export default function CombatOverlay({ scene, onConfirm, onAmountsChange, onLifeImpact, onDone }) {
  return (
    <AnimatePresence>
      {scene && (
        <Panel
          key={scene.combatId}
          scene={scene}
          onConfirm={onConfirm}
          onAmountsChange={onAmountsChange}
          onLifeImpact={onLifeImpact}
          onDone={onDone}
        />
      )}
    </AnimatePresence>
  );
}

// The participant id an event lands on: a struck card, or the seat's
// life total (rendered as the avatar/site portrait the host enriched in).
// A lifeLoss beat prefers the struck card's instanceId so a DEFENDING
// avatar's beat lands on its existing defender portrait instead of
// spawning a duplicate "Life" tile for the same avatar.
const eventPid = (e) => (e.kind === 'damage' ? e.targetId : (e.instanceId || `life:${e.seat}`));

function Panel({ scene, onConfirm, onAmountsChange, onLifeImpact, onDone }) {
  const { mode, attacker, events } = scene;
  const playing = mode === 'playback';
  const interactive = mode === 'assign';
  const power = scene.power ?? null;
  // Stable pid for the attacker portrait even when its id is unknown.
  const atkPid = attacker?.id || '__atk__';

  // Player-chosen playback multiplier, read ONCE for the life of the
  // panel. Every beat and every animation duration below divides by it.
  // Reading it live would let a mid-fight settings change rebuild the
  // derived timings and restart in-flight animations (see the
  // keyframe-identity note above DamageBurst).
  const speed = useRef(getAccessibilitySettings().combatSpeed || 1).current;
  const leadMs = LEAD_MS / speed;
  const beatMs = BEAT_MS / speed;
  const holdMs = HOLD_MS / speed;
  const dashMs = DASH_MS / speed;

  // ── Assignment state (assign mode owns it; spectate/locked display
  // the streamed/decided amounts from the scene instead) ──
  const [localAmounts, setAmounts] = useState(() => {
    const a = {};
    for (const d of scene.defenders || []) a[d.id] = 0;
    return a;
  });
  const amounts = interactive ? localAmounts : (scene.liveAmounts || localAmounts);

  // Acting side: stream every change so the opponent's spectate panel
  // tracks the allocation live.
  useEffect(() => {
    if (interactive) onAmountsChange?.(localAmounts);
  }, [interactive, localAmounts]);

  const total = Object.values(amounts).reduce((s, n) => s + n, 0);
  const remaining = power != null ? power - total : 0;
  const valid = remaining === 0;

  const bump = (id, delta) => {
    setAmounts((prev) => {
      const next = Math.max(0, (prev[id] || 0) + delta);
      const others = Object.entries(prev).reduce((s, [k, v]) => (k === id ? s : s + v), 0);
      if (others + next > power) return prev;
      return { ...prev, [id]: next };
    });
  };

  // Kill-priority split (engine/core/autoAssign): destroy as many
  // defenders as the power allows. Never waste everything on a
  // defender it can't kill when a smaller one is killable.
  const autoAssign = () => setAmounts(autoAssignDamage(power, scene.defenders || []));

  const confirm = () => {
    if (!valid) return;
    const map = new Map();
    for (const d of scene.defenders || []) map.set(d.id, amounts[d.id] || 0);
    onConfirm(map);
  };

  // ── Participant roster: the assign-era defenders (so the portraits
  // carry over when the panel morphs into playback), unioned with any
  // party the event stream strikes that wasn't in the assign list
  // (flat 1v1 fights, site/avatar life loss). The attacker is never in
  // this list. Strike-backs land on the attacker portrait. ──
  const roster = [];
  const byId = new Map();
  for (const d of scene.defenders || []) {
    const p = { ...d, isLife: false };
    byId.set(d.id, p);
    roster.push(p);
  }
  for (const e of events || []) {
    const pid = eventPid(e);
    if (pid === attacker?.id || byId.has(pid)) continue;
    const p = {
      id: pid,
      name: e.targetName || t('Unit'),
      imageUrl: e.targetImageUrl || null,
      foiling: e.targetFoiling || null,
      isSite: !!e.targetIsSite,
      def: null,
      isLife: e.kind === 'lifeLoss',
    };
    byId.set(pid, p);
    roster.push(p);
  }

  // ── Struck-side fit ──
  // The defender grid lives in a fixed DEF_W × DEF_H box; the cards
  // scale down (uniformly, gaps included) so every defender is always
  // on screen at once. The stage itself never grows, wraps a scrollbar,
  // or moves the buttons. Fights that ever had an assign step reserve
  // the +/- row height in every mode (hidden when not interactive) so
  // the assign → locked → playback morphs can't shift the rows.
  const hasAssign = power != null;
  const { scale: defScale, cols: defCols } = fitDefenders(roster.length, hasAssign);
  const defRows = [];
  for (let i = 0; i < roster.length; i += defCols) defRows.push(roster.slice(i, i + defCols));
  const btnH = Math.max(2, 3 * defScale);

  // ── Playback engine ──
  // One beat per event: measure the live offset between the striker's
  // and the victim's portraits, dash, land the impact at IMPACT_FRAC of
  // the dash, return. All hit bookkeeping is keyed by display pid (the
  // attacker's hits live under atkPid).
  const [strike, setStrike] = useState(null);   // { key, actors:[{pid,dx,dy}], victimPid }
  const [impacts, setImpacts] = useState({});   // pid -> { key, dxn, dyn, amount, magic }
  const [shakeFx, setShakeFx] = useState(null); // { key, amp }
  const [hits, setHits] = useState({});         // pid -> cumulative damage
  const [bursts, setBursts] = useState([]);     // { id, pid, amount }
  const [deaths, setDeaths] = useState({});     // pid -> true
  const portraitRefs = useRef({});
  const done = useRef(false);
  const playStart = useRef(0);

  const pidFor = (raw) => (raw === attacker?.id ? atkPid : raw);

  // An eventless resolved scene (every strike fully absorbed with no
  // compensating beat) must still self-dismiss. The beat-driven effect
  // below never schedules its onDone with zero events, which stranded
  // both players on CLICK TO SKIP (GH-69's second symptom).
  useEffect(() => {
    if (!playing || events?.length) return;
    playStart.current = Date.now();
    const t = setTimeout(() => {
      if (!done.current) { done.current = true; onDone(); }
    }, leadMs + holdMs);
    return () => clearTimeout(t);
  }, [playing]);

  // Instant result mode (replay): every hit and death lands in one frame and
  // the tally HOLDS. No strike choreography, no self-dismiss timer. The scene
  // is position-driven: it stays up while the playhead sits on the fight's
  // entry and tears down when the playhead moves off it (or on click).
  useEffect(() => {
    if (!playing || !events?.length || !scene.instant) return;
    playStart.current = Date.now();
    const allHits = {};
    const allDeaths = {};
    for (const e of events) {
      const pid = pidFor(eventPid(e));
      allHits[pid] = (allHits[pid] || 0) + e.amount;
      if (e.targetDied) allDeaths[pid] = true;
    }
    setHits(allHits);
    setDeaths(allDeaths);
  }, [playing]);

  useEffect(() => {
    if (!playing || !events?.length || scene.instant) return;
    playStart.current = Date.now();
    const timers = [];
    const after = (ms, fn) => timers.push(setTimeout(fn, ms));

    // Which portraits swing for this event. An aggregated strike-back
    // (damage to the attacker, no sourceId) is every defender at once. An
    // AVATAR attacker's strike-back rides a lifeLoss beat carrying its own
    // instanceId (qa#82), so that shape gets the same defender swarm.
    const actorsFor = (e) => {
      const strikeBackOnAttacker = (e.kind === 'damage' && pidFor(e.targetId) === atkPid)
        || (e.kind === 'lifeLoss' && e.instanceId && pidFor(e.instanceId) === atkPid);
      if (strikeBackOnAttacker) {
        const defs = roster.filter((p) => !p.isLife).map((p) => p.id);
        return defs.length ? defs : [atkPid];
      }
      if (e.sourceId && byId.has(e.sourceId)) return [e.sourceId];
      return [atkPid];
    };

    events.forEach((e, i) => {
      after(leadMs + i * beatMs, () => {
        const victimPid = pidFor(eventPid(e));
        const victimEl = portraitRefs.current[victimPid];
        const vRect = victimEl?.getBoundingClientRect();
        // Physical strikers dash into the victim and stop short (cards
        // collide, they don't eclipse); Spellcasters stay put and send
        // an arcane bolt all the way to the victim's center instead.
        const reach = e.magic ? 0.96 : 0.8;
        const actors = actorsFor(e).filter((pid) => pid !== victimPid).map((pid) => {
          const aRect = portraitRefs.current[pid]?.getBoundingClientRect();
          if (!aRect || !vRect) return { pid, dx: 0, dy: 0 };
          return {
            pid,
            dx: (vRect.left + vRect.width / 2 - (aRect.left + aRect.width / 2)) * reach,
            dy: (vRect.top + vRect.height / 2 - (aRect.top + aRect.height / 2)) * reach,
          };
        });
        setStrike({ key: i, actors, victimPid, magic: !!e.magic, amount: e.amount });
        // Knockback continues the first striker's direction of travel.
        const lead = actors.find((a) => a.dx || a.dy);
        const len = lead ? Math.hypot(lead.dx, lead.dy) : 0;
        const dxn = len ? lead.dx / len : 1;
        const dyn = len ? lead.dy / len : 0;

        // Ward changes the RESULT, not the swing. A warded hit lands with its
        // full would-be force (sound, shake, flash, sparks all key off
        // `force`), it just deals no damage and shows "Warded" in place of a
        // damage number.
        const force = (e.warded || e.deathBlow) ? (e.power || e.amount) : e.amount;
        after(dashMs * IMPACT_FRAC, () => {
          setHits((h) => ({ ...h, [victimPid]: (h[victimPid] || 0) + e.amount }));
          setBursts((b) => [...b, { id: `${i}-${victimPid}`, pid: victimPid, amount: e.amount, warded: !!e.warded, deathBlow: !!e.deathBlow }]);
          setImpacts((m) => ({ ...m, [victimPid]: { key: i, dxn, dyn, amount: force, magic: !!e.magic } }));
          setShakeFx({ key: i, amp: force >= BIG_HIT ? 9 : 3 + force });
          playSound(combatImpactKey(force, e.magic));
          // Life loss landed THIS frame. Release the held HUD life
          // countdown so the number starts draining exactly on impact.
          const lifeSeat = e.kind === 'lifeLoss' ? e.seat : e.avatarSeat;
          if (lifeSeat) onLifeImpact?.(lifeSeat);
          if (e.targetDied && !events.slice(i + 1).some((x) => x.kind === 'damage' && x.targetId === e.targetId)) {
            after(beatMs * 0.5, () => setDeaths((d) => ({ ...d, [victimPid]: true })));
          }
        });
      });
    });
    after(playbackDurationMs(events.length, speed), () => {
      if (!done.current) { done.current = true; onDone(); }
    });
    return () => timers.forEach(clearTimeout);
  }, [playing]);

  const skip = () => {
    if (!playing || done.current) return;
    // A click already in motion when the stage mounts must not dismiss
    // a fight the player never saw. Honor skips only once it's had a
    // moment on screen.
    if (Date.now() - playStart.current < 500) return;
    done.current = true;
    onDone();
  };

  const attackerTaken = hits[atkPid] || 0;
  const attackerDead = playing && !!scene.attackerDied && attackerTaken > 0;

  // Stable per impact. See the keyframe-identity note above DamageBurst.
  const shakeAnim = useMemo(() => (shakeFx
    ? { x: [0, -shakeFx.amp, shakeFx.amp * 0.7, -shakeFx.amp * 0.4, 0], y: [0, shakeFx.amp * 0.6, -shakeFx.amp * 0.35, shakeFx.amp * 0.2, 0] }
    : { x: 0, y: 0 }), [shakeFx]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25 } }}
      onMouseDown={(e) => { e.stopPropagation(); skip(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: Z,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Replay: the feed sidebar owns the left ~25cqw, so the stage centers
        // in (and scales to) the visible area beside it instead of the full
        // canvas. Live fights keep the full-canvas center.
        paddingLeft: scene.instant ? '25.5cqw' : 0,
        background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.78) 100%)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: scene.instant ? 0.8 : 0.94, y: 10 }}
        animate={{ opacity: 1, scale: scene.instant ? 0.86 : 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 420, damping: 30 }}
        role="dialog"
        aria-label={playing ? t('Combat resolution') : t('Assign attack damage')}
        style={{
          ...TEXTURED_PANEL_STYLE,
          position: 'relative',
          borderRadius: 'var(--radius-xl)',
          padding: '2.4cqw 3.2cqw 2.4cqw',
          // Constant footprint. The stage is the same size for every
          // fight; its contents scale to fit, never the reverse.
          width: `${PANEL_W}cqw`,
          height: `${PANEL_H}cqw`,
 display: 'flex',
 flexDirection: 'column',
 }}
 >
 <FourCorners knots knotRotation={0} />
 {/* Impact shake wrapper. Jolts the whole stage on every hit,
 amplitude scaled to the damage. Retriggered by swapping in a
 fresh memoized target per impact. NEVER by re-keying: a key
 change here would remount the entire stage mid-beat,
 restarting the in-flight dash and replaying every visible
 damage burst (every hit would appear to land twice). */}
 <motion.div
 animate={shakeAnim}
 transition={{ duration: 0.34 / speed }}
 style={{
 display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.1cqw',
 flex: 1, minHeight: 0, alignSelf: 'stretch',
 }}
 >
 <div className="combat-prompt-title" style={{ padding: '0.1cqw 1cqw', fontSize: '2.8cqw' }}>
 {playing ? t('Combat') : t('Assign Damage')}
 </div>
 <OrnamentalDivider className="self-stretch -mt-2" />

 {/* Power pool rail. One pip per point, draining as it's
 assigned. The slot height is reserved even on fights that
 never had an assign step, so the title/arena/footer sit at
 identical positions in every fight. */}
 <div style={{ height: '1.5cqw', display: 'flex', gap: '0.4cqw', alignItems: 'center' }}>
 {power > 0 && (
 <>
 {Array.from({ length: power }, (_, i) => (
 <div key={i} style={{
 width: '1.3cqw', height: '1.3cqw', borderRadius: '50%',
 backgroundColor: i < remaining ? 'rgb(214, 69, 69)' : 'rgb(58, 46, 28)',
 boxShadow: i < remaining
 ? '0 0 0.5cqw rgba(214, 69, 69, 0.6), inset 0 0.08cqw 0.12cqw rgba(255,255,255,0.25)'
 : 'inset 0 0.1cqw 0.2cqw rgba(0,0,0,0.6)',
 transition: 'background-color 140ms ease-out, box-shadow 140ms ease-out',
 }} />
 ))}
 {!playing && (
 <div style={{
 marginLeft: '0.8cqw',
 color: remaining ? 'rgb(232, 96, 96)' : 'rgb(120, 200, 140)',
 fontSize: '0.85cqw', fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.8)',
 }}>
 {remaining ? t('{n} to assign', { n: remaining }) : t('All assigned')}
                </div>
              )}
              </>
            )}
          </div>

          {/* The arena: a recessed stone surface the fight stands on.
              carved into the tablet rather than floating over it. It
              fills the panel's remaining height, so it too is a fixed
              region. The fight scales inside it. */}
          <div style={{
            ...SUB_PANEL_STYLE,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3.2cqw',
            marginTop: '0.5cqw',
            padding: '1.2cqw 2.4cqw',
            alignSelf: 'stretch', flex: 1, minHeight: 0,
          }}>
            {/* Attacker. Fixed full-size slot; there is only ever one. */}
            <div style={{ width: `${COL_W}cqw`, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <StrikeColumn pid={atkPid} strike={strike} speed={speed}>
                <Fighter
                  speed={speed}
                  innerRef={(el) => { portraitRefs.current[atkPid] = el; }}
                  name={attacker?.name || t('Attacker')}
                  imageUrl={attacker?.imageUrl}
                  foiling={attacker?.foiling}
                  isSite={attacker?.isSite}
                  caption={t('Attacker')}
                  dead={attackerDead}
                  stamp={attackerDead ? 'Destroyed' : null}
                  bursts={bursts.filter((b) => b.pid === atkPid)}
                  impact={impacts[atkPid] || null}
                  badge={attackerTaken > 0
                    ? { value: `-${attackerTaken}`, label: t('Damage taken by attacker'), color: 'rgb(232, 96, 96)' }
                    : power != null
                      ? { value: power, label: t('Attack power'), color: ACCENT_GOLD }
                      : null}
                />
                {/* Phantom +/- slot mirroring the defenders' reserved
                    row so the attacker's card midline matches a
                    single full-size defender's exactly. */}
                {hasAssign && <div style={{ height: `${buttonSlotH(1)}cqw` }} />}
              </StrikeColumn>
            </div>

            {/* Clash divider */}
            <div style={{
              fontFamily: "'FantaisieArtistique', cursive",
              fontSize: '3cqw', color: 'rgb(150, 120, 70)',
              textShadow: '0 2px 6px rgba(0,0,0,0.9)',
              letterSpacing: '0.06em',
              flexShrink: 0,
            }}>VS</div>

            {/* Struck side: defenders during assignment, every struck
                party during playback. A fixed box. Rows of uniformly
                scaled columns, never a scrollbar, never a reflow. */}
            <div style={{
              width: `${DEF_W}cqw`, height: `${DEF_H}cqw`, flexShrink: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: `${ROW_GAP * defScale}cqw`,
            }}>
              {defRows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: `${COL_GAP * defScale}cqw`, justifyContent: 'center' }}>
                  {row.map((d) => {
                    const assigned = amounts[d.id] || 0;
                    const lethal = !playing && d.def != null && assigned >= d.def;
                    const taken = hits[d.id] || 0;
                    const dead = playing ? !!deaths[d.id] : lethal;
                    return (
                      <StrikeColumn key={d.id} pid={d.id} strike={strike} scale={defScale} speed={speed}>
                        <Fighter
                          speed={speed}
                          innerRef={(el) => { portraitRefs.current[d.id] = el; }}
                          name={d.name}
                          imageUrl={d.imageUrl}
                          foiling={d.foiling}
                          isSite={d.isSite}
                          caption={playing && d.isLife ? t('Life')
                            // A defending avatar has no defence: what you
                            // assign to it comes off its controller's life,
                            // and no tank can absorb that (the engine only
                            // redirects card damage). So this outranks
                            // `redirectTo` rather than sitting under it.
                            : d.isAvatar ? t('Life loss')
                            : (!playing && d.redirectTo) ? t('Absorbed by {name}', { name: d.redirectTo.name })
                            : d.def != null ? t('Defense {n}', { n: d.def }) : t('Defender')}
                          redirectTo={!playing ? d.redirectTo : null}
                          dead={dead}
                          stamp={playing ? (deaths[d.id] ? 'Destroyed' : null) : (lethal ? 'Lethal' : null)}
                          bursts={bursts.filter((b) => b.pid === d.id)}
                          impact={impacts[d.id] || null}
                          scale={defScale}
                          badge={playing
                            ? (taken > 0 ? { value: `-${taken}`, label: t('Damage taken by {name}', { name: d.name }), color: 'rgb(232, 96, 96)' } : null)
                            : {
                              value: assigned,
                              label: t('Damage assigned to {name}', { name: d.name }),
                              color: lethal ? 'rgb(232, 96, 96)' : assigned > 0 ? '#ffdc82' : 'rgb(140, 125, 95)',
                            }}
                          onClick={interactive ? () => bump(d.id, 1) : null}
                        />
                        {/* Reserved in every mode (hidden when not
                            interactive) so mode morphs never shift
                            the rows. Mirrored by buttonSlotH above. */}
                        {hasAssign && (
                          <div style={{
                            height: `${btnH}cqw`, marginTop: '0.5cqw',
 display: 'flex', gap: '0.5cqw', alignItems: 'center',
 visibility: interactive ? 'visible' : 'hidden',
 }}>
 <StoneButton variant="secondary" heightCqw={btnH} onClick={() => bump(d.id, -1)} disabled={assigned === 0}>−</StoneButton>
 <StoneButton variant="secondary" heightCqw={btnH} onClick={() => bump(d.id, 1)} disabled={remaining === 0}>+</StoneButton>
 </div>
 )}
 </StrikeColumn>
 );
 })}
 </div>
 ))}
 </div>
 </div>

 {/* Footer. Fixed-height slot so mode morphs never shift the
 panel. Buttons for the acting player; everyone else gets a
 single small-caps status line. */}
 <div style={{
 minHeight: '3.2cqw', marginTop: '0.6cqw',
 display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1cqw',
 }}>
 {interactive ? (
 <>
 <StoneButton variant="secondary" onClick={autoAssign}>{t('Auto')}</StoneButton>
 <StoneButton variant="primary" onClick={confirm} disabled={!valid}>
 {t('Strike')}
 </StoneButton>
 </>
 ) : (
 <div style={{
 color: 'rgb(150, 132, 100)', fontSize: '0.8cqw', fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 }}>
 {playing ? (scene.instant ? t('Click to continue') : t('Click to skip'))
 : mode === 'locked' ? t('Resolving')
 : t('Opponent assigns the damage')}
 </div>
 )}
 </div>
 </motion.div>
 </motion.div>
 </motion.div>
 );
}

// Wraps one portrait column and drives its strike when it's a striker
// in the current beat. Physical strikers dash: windup away from the
// target, rush across the panel to the victim's measured position
// (collide, don't eclipse), brief hold at the point of impact, then
// settle back home. Spellcaster strikers don't move. They pulse in
// place and launch an arcane bolt that crosses the panel instead,
// timed to arrive on the impact frame. Retriggered per beat by a fresh
// memoized target, never by re-keying (see the shake wrapper note).
function StrikeColumn({ pid, strike, scale = 1, speed = 1, children }) {
 const dash = strike?.actors?.find((a) => a.pid === pid) || null;
 const magic = !!(dash && strike.magic);
 // Visual magic tier runs 1..7 (audio caps at magic-3).
 const tier = magic ? Math.min(7, Math.max(1, strike.amount || 1)) : 1;
 // Stable per beat. See the keyframe-identity note above DamageBurst.
 const anim = useMemo(() => {
 if (!dash || (!dash.dx && !dash.dy)) return { x: 0, y: 0, rotate: 0, scale: 1 };
 if (magic) {
 // Casting gesture: a rising pulse, no travel. Bigger wind-up
 // for bigger spells.
 return { x: 0, y: [0, -(8 + tier * 1.6), 0], rotate: 0, scale: [1, 1.05 + tier * 0.015, 1] };
 }
 return {
 x: [0, -dash.dx * 0.1, dash.dx, dash.dx * 0.96, 0],
 y: [0, -dash.dy * 0.1 - 8, dash.dy, dash.dy * 0.96, 0],
 rotate: [0, -3, 4, 2, 0],
 scale: [1, 1.06, 1.03, 1.02, 1],
 };
 }, [strike?.key, dash?.dx, dash?.dy, magic, tier]);
 return (
 <motion.div
 animate={anim}
 transition={!dash
 ? { duration: 0.2 / speed }
 : magic
 // The casting pulse has to finish on the impact frame like the
 // physical dash does, so it scales with the speed too.
 ? { duration: 0.5 / speed, ease: 'easeOut' }
 : { duration: DASH_MS / speed / 1000, times: [0, 0.26, IMPACT_FRAC, 0.56, 1], ease: 'easeInOut' }}
 style={{
 display: 'flex', flexDirection: 'column', alignItems: 'center',
 position: 'relative', zIndex: dash ? 6 : 1,
 }}
 >
 {children}
 {magic && (dash.dx || dash.dy) ? (
 <ArcaneBolt
 key={`bolt-${strike.key}-${pid}`}
          dx={dash.dx}
          dy={dash.dy}
          tier={tier}
          scale={scale}
          speed={speed}
        />
      ) : null}
    </motion.div>
  );
}

// The Spellcaster's strike: a glowing bolt that launches from the
// caster's portrait, arcs across the panel, and dissolves into the
// victim exactly on the impact frame (where the arcane flash, ring,
// and magic-N sample land). Mounts fresh per beat. It's leaf content,
// so the re-key can't disturb anything else. The keyframes are
// memoized once per mount so the impact-time re-renders can't restart
// the flight (see the keyframe-identity note above DamageBurst).
function ArcaneBolt({ dx, dy, tier = 1, scale = 1, speed = 1 }) {
  // The bolt must dissolve ON the impact frame, so its flight and its
  // dissolve tail both ride the same speed as the beat that scheduled it.
  const impactMs = (DASH_MS / speed) * IMPACT_FRAC;
  const totalMs = impactMs + 180 / speed;
  // Visual tiers keep growing past the three audio tiers (the samples
  // cap at magic-3; the spectacle doesn't): 1..7, tapered so a 7-power
  // bolt is huge without filling the panel. Tier 4+ adds afterimage
  // orbs trailing the core so raw size isn't the only power cue.
  // A bolt cast from a shrunken defender column shrinks with its
  // caster, floored so it stays a visible projectile.
  const s = (1 + (tier - 1) * 0.42) * Math.max(0.6, scale);
  const trailCount = Math.max(0, tier - 3);
  // One memoized keyframe set per orb (core + trail). See the
  // keyframe-identity note above DamageBurst.
  const anims = useMemo(() => {
    const len = Math.hypot(dx, dy) || 1;
    // A slight perpendicular arc so the bolt lobs rather than beelines.
    const arcX = (-dy / len) * 30;
    const arcY = (dx / len) * 30;
    const mk = (peak) => ({
      x: [0, dx * 0.5 + arcX, dx, dx],
      y: [0, dy * 0.5 + arcY, dy, dy],
      opacity: [0, peak, peak, 0],
      scale: [0.4, 1, 1.05, 1.7 + (tier - 1) * 0.35],
    });
    return [mk(1), ...Array.from({ length: trailCount }, (_, i) => mk(0.7 - i * 0.12))];
  }, []);
  const tImpact = impactMs / totalMs;
  const times = [0, tImpact * 0.55, tImpact, 1];
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  return (
    <>
      {anims.map((anim, i) => {
        const scaleMul = i === 0 ? 1 : 0.62 - (i - 1) * 0.1;
        return (
          <motion.div
            key={i === 0 ? 'core' : `trail-${i}`}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
            animate={anim}
            transition={{ duration: totalMs / 1000, times, ease: 'easeIn', delay: (i * 0.055) / speed }}
            style={{
              position: 'absolute', left: '50%', top: `${(CARD_H * scale) / 2}cqw`,
              zIndex: 9, pointerEvents: 'none',
            }}
          >
            {i === 0 && (
              /* Tail streak, pointed along the direction of travel.
                 core orb only. */
              <div style={{
                position: 'absolute', right: '0.3cqw', top: '50%',
                width: `${3.6 * s}cqw`, height: `${0.5 * s}cqw`,
                transform: `translateY(-50%) rotate(${angle}deg)`,
                transformOrigin: 'right center',
                background: `linear-gradient(270deg, ${MAGIC_FX} 0%, transparent 100%)`,
                opacity: 0.8,
                borderRadius: `${0.25 * s}cqw`,
              }} />
            )}
            <div style={{
              position: 'absolute',
              left: `${-0.7 * s * scaleMul}cqw`, top: `${-0.7 * s * scaleMul}cqw`,
              width: `${1.4 * s * scaleMul}cqw`, height: `${1.4 * s * scaleMul}cqw`,
              borderRadius: '50%',
              background: `radial-gradient(circle, #fff 0%, ${MAGIC_FX} 45%, transparent 75%)`,
              boxShadow: `0 0 ${1.2 * s * scaleMul}cqw ${MAGIC_FX}, 0 0 ${0.4 * s * scaleMul}cqw #fff`,
            }} />
          </motion.div>
        );
      })}
    </>
  );
}

function Fighter({ name, imageUrl, caption, dead, stamp, bursts, impact, badge, onClick, innerRef, scale = 1, speed = 1, redirectTo = null, foiling = null, isSite = false }) {
  // Stable per impact. See the keyframe-identity note above DamageBurst.
  const kbAnim = useMemo(() => (impact
    ? {
      x: [0, impact.dxn * (9 + impact.amount * 3.5), impact.dxn * 4, 0],
      y: [0, impact.dyn * (9 + impact.amount * 3.5), impact.dyn * 4, 0],
      rotate: [0, impact.dxn * 4, impact.dxn * 1.5, 0],
    }
    : { x: 0, y: 0, rotate: 0 }), [impact]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      {/* Knockback: the struck card is shoved along the striker's
          direction of travel. Retriggered by the fresh memoized target
          per impact, never by re-keying (see the shake wrapper note). */}
      <motion.div
        animate={kbAnim}
        transition={{ duration: 0.4 / speed, ease: 'easeOut' }}
        style={{ position: 'relative' }}
      >
        <motion.div
          animate={dead && stamp === 'Destroyed'
            ? { rotate: 8, y: '1.2cqw', filter: 'saturate(0.2) brightness(0.5)' }
            : { rotate: 0, y: 0, filter: 'brightness(1)' }}
          transition={{ duration: 0.5 / speed }}
        >
          <div
            ref={innerRef}
            role={onClick ? 'button' : undefined}
            aria-label={onClick ? t('Assign damage to {name}', { name }) : undefined}
            onClick={onClick || undefined}
          >
            <CardPortrait name={name} imageUrl={imageUrl} dead={dead} scale={scale} foiling={foiling} isSite={isSite}>
              {badge && <StatBadge value={badge.value} label={badge.label} color={badge.color} big scale={scale} />}
              <ImpactFX impact={impact} scale={scale} speed={speed} />
            </CardPortrait>
          </div>
        </motion.div>

        {/* Tank redirect (Sir Gawain): the ally is still assigned the hit, but
            it lands on the tank. Inset the tank's card image at the corner with
            an arrow so the assigner sees where the damage actually goes. */}
        {redirectTo && (
          <div
            title={t('{name} absorbs this damage', { name: redirectTo.name })}
            style={{
              position: 'absolute', right: `${-1 * scale}cqw`, bottom: `${-1 * scale}cqw`,
              zIndex: 6, pointerEvents: 'none', borderRadius: RADIUS.card,
              boxShadow: `0 0 0 ${0.18 * scale}cqw #ffdc82, 0 ${0.3 * scale}cqw ${0.8 * scale}cqw rgba(0,0,0,0.6)`,
            }}
          >
            <CardPortrait name={redirectTo.name} imageUrl={redirectTo.imageUrl} scale={scale * 0.5} foiling={redirectTo.foiling} isSite={redirectTo.isSite} />
            <div style={{
              position: 'absolute', top: `${-0.9 * scale}cqw`, left: '50%', transform: 'translateX(-50%)',
              fontSize: `${1.4 * scale}cqw`, lineHeight: 1, color: '#ffdc82',
              textShadow: '0 0 0.3cqw #000', fontWeight: 700,
            }}>↳</div>
          </div>
        )}

        <AnimatePresence>
          {bursts.slice(-2).map((b) => <DamageBurst key={b.id} amount={b.amount} warded={b.warded} deathBlow={b.deathBlow} scale={scale} speed={speed} />)}
        </AnimatePresence>

        {stamp && <FateStamp label={stamp === 'Destroyed' ? t('Destroyed') : t('Lethal')} scale={scale} />}
      </motion.div>
      <NameBlock name={name} caption={caption} scale={scale} />
    </div>
  );
}
